// CFG-IA.B1 (#1541) — canonical storage-provider selector component.
//
// Member-visible read surface; canEdit=false renders radios disabled.
// Instant-save on change (POST /tenants/:id/infrastructure-config), mirrors
// the EstimationCard pattern. SharePoint rendered but disabled ("coming soon")
// so the provider abstraction is preserved when full support lands.
import { useState, useEffect } from 'react';
import { apiFetch } from '../../lib/api';

export type StorageProvider = 'GOOGLE_DRIVE' | 'SHAREPOINT';

interface ExistingConfig {
  storageProvider: string | null;
}

interface Props {
  tenantId:  string;
  canEdit?:  boolean;
  showIntro?: boolean;
  onSaved?:  (provider: StorageProvider) => void;
}

const OPTIONS: Array<{ value: StorageProvider; label: string; comingSoon?: boolean }> = [
  { value: 'GOOGLE_DRIVE', label: 'Google Drive' },
  { value: 'SHAREPOINT',   label: 'SharePoint',  comingSoon: true },
];

export default function StorageProviderCard({ tenantId, canEdit = true, showIntro = true, onSaved }: Props) {
  const [selected, setSelected] = useState<StorageProvider>('GOOGLE_DRIVE');
  const [loaded,   setLoaded]   = useState(false);

  useEffect(() => {
    apiFetch<ExistingConfig>(`/tenants/${tenantId}/infrastructure-config`)
      .then(cfg => {
        if (cfg.storageProvider === 'SHAREPOINT') {
          setSelected('SHAREPOINT');
        } else {
          setSelected('GOOGLE_DRIVE');
        }
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [tenantId]);

  const handleChange = (provider: StorageProvider) => {
    if (!canEdit || !loaded) return;
    setSelected(provider);
    void apiFetch(`/tenants/${tenantId}/infrastructure-config`, {
      method: 'POST',
      body:   JSON.stringify({ storageProvider: provider }),
    }).then(() => onSaved?.(provider)).catch(() => {});
  };

  return (
    <div>
      {showIntro && (
        <>
          <h3 style={{
            fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600,
            color: 'var(--text)', marginBottom: 6,
          }}>
            Storage Provider
          </h3>
          <p style={{
            fontFamily: 'var(--font-body)', fontSize: 13,
            color: 'var(--text-secondary)', marginBottom: 16,
          }}>
            Choose where BluePrint stores project artefacts and shared documents.
          </p>
        </>
      )}

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {OPTIONS.map(opt => {
          const isSelected = selected === opt.value;
          const isDisabled = !canEdit || opt.comingSoon;
          return (
            <label
              key={opt.value}
              data-testid={`settings-storage-${opt.value}`}
              style={{
                display:       'flex',
                alignItems:    'center',
                gap:           8,
                padding:       '9px 16px',
                borderRadius:  'var(--radius-sm)',
                border:        `1px solid ${isSelected && !opt.comingSoon ? 'var(--primary)' : 'var(--border)'}`,
                background:    isSelected && !opt.comingSoon ? 'rgba(124,58,237,0.06)' : 'transparent',
                cursor:        isDisabled ? 'not-allowed' : 'pointer',
                fontFamily:    'var(--font-body)',
                fontSize:      14,
                fontWeight:    isSelected && !opt.comingSoon ? 600 : 400,
                color:         isDisabled
                  ? 'var(--text-secondary)'
                  : isSelected ? 'var(--primary)' : 'var(--text)',
                opacity:       opt.comingSoon ? 0.55 : 1,
                transition:    'border-color 150ms, background 150ms',
              }}
            >
              <input
                type="radio"
                name="storageProvider"
                value={opt.value}
                checked={isSelected && !opt.comingSoon}
                disabled={isDisabled}
                onChange={() => handleChange(opt.value)}
                style={{ accentColor: 'var(--primary)' }}
              />
              {opt.label}
              {opt.comingSoon && (
                <span style={{
                  marginLeft: 6, fontSize: 10, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.05em',
                  color: 'var(--text-secondary)',
                }}>
                  coming soon
                </span>
              )}
            </label>
          );
        })}
      </div>

      <p style={{
        fontFamily: 'var(--font-body)', fontSize: 12,
        color: 'var(--text-secondary)', marginTop: 10,
      }}>
        Connect, reconnect or disconnect Google Drive in{' '}
        <a href="/settings/integrations/gdrive" style={{ color: 'var(--primary)' }}>
          Settings → Google Drive
        </a>.
      </p>
    </div>
  );
}
